import React, { useEffect, useState } from "react";
import styles from "./ExpandableMenu.module.scss";
import { ReactComponent as LockIcon } from "../../icons/lock.svg";
import classNames from "classnames";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
} from "lucide-react";

const ExpandableMenu = ({
  treeData,
  onNodeClick,
  selectedPageId,
}) => {
  // State to keep track of expanded nodes
  const [expanded, setExpanded] = useState({});

  // Expand all parent nodes of the selected page
  useEffect(() => {
    if (selectedPageId && treeData) {
      const findNodePath = (nodes, targetPageId, path = []) => {
        if (!Array.isArray(nodes)) return null;

        for (const node of nodes) {
          if (String(node.page_id) === String(targetPageId)) {
            return [...path, node.page_id];
          }
          if (node.children?.length > 0) {
            const result = findNodePath(node.children, targetPageId, [
              ...path,
              node.page_id,
            ]);
            if (result) return result;
          }
        }
        return null;
      };

      const pathToNode = findNodePath(treeData, selectedPageId);
      if (pathToNode) {
        const newExpanded = {};
        pathToNode.forEach((pageId) => {
          newExpanded[pageId] = true;
        });
        setExpanded(newExpanded);
      }
    }
  }, [selectedPageId, treeData]);

  const handleArrowClick = (pageId) => {
    const isExpanded = expanded[pageId];
    setExpanded((prev) => ({
      ...prev,
      [pageId]: !isExpanded, // Toggle the expansion state
    }));
  };

  const handlePageNameClick = (node) => {
    onNodeClick(node.page_id, node.page_name); // Fetch data for the clicked page
  };

  // const handleNodeClick = (node) => {
  //   const isExpanded = expanded[node.page_id];
  //
  //   // Always fetch data when a node is clicked, even if it's already selected
  //   onNodeClick(node.page_id, node.page_name);
  //
  //   // Toggle expansion only if the node is not already expanded
  //   if (isExpanded) {
  //     setExpanded((prev) => ({
  //       ...prev,
  //       [node.page_id]: false, // Collapse the node
  //     }));
  //   } else {
  //     setExpanded((prev) => ({
  //       ...prev,
  //       [node.page_id]: true, // Expand the node
  //     }));
  //   }
  // };

  // Recursive function to render the tree
  const renderTree = (node, level = 0) => {
    if (!node) return null; // Skip invalid nodes

    const isPrivateDisabled = node.page_id === null && node.private === true;
    const isExpanded = expanded[node.page_id];
    const isSelected = String(selectedPageId) === String(node.page_id);
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const RowIcon = hasChildren ? (isExpanded ? FolderOpen : Folder) : FileText;
    const ExpandIcon = isExpanded ? ChevronDown : ChevronRight;

    return (
      <div
        key={node.page_id || node.page_name}
      >
        <div
          className={classNames(
            styles.row,
            isSelected && styles.selectedRow,
            isPrivateDisabled && styles.disabledRow,
          )}
          style={{ paddingLeft: `${level * 20 + 16}px` }}
          data-level={level}
          data-has-children={hasChildren ? "true" : "false"}
          onClick={() => {
            if (isPrivateDisabled) return;
            handlePageNameClick(node);
          }}
        >
          {hasChildren ? (
            <button
              type="button"
              className={styles.expandButton}
              onClick={(e) => {
                e.stopPropagation();
                handleArrowClick(node.page_id);
              }}
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              <ExpandIcon size={16} className={styles.icon} />
            </button>
          ) : (
            <span className={styles.expandPlaceholder} />
          )}

          <span className={styles.itemIcon} aria-hidden="true">
            <RowIcon size={16} className={styles.icon} />
          </span>

          <button
            type="button"
            className={classNames(
              styles.pageButton,
              isPrivateDisabled && styles.disabled,
            )}
            onClick={
              isPrivateDisabled
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    handlePageNameClick(node);
                  }
            }
            disabled={isPrivateDisabled}
          >
            {isPrivateDisabled && <LockIcon className={styles.lockIcon} />}
            <span className={styles.pageText}>{node.page_name}</span>
          </button>
        </div>
        {hasChildren && isExpanded && (
          <div className={styles.children}>
            {node.children.map((child) => renderTree(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.tree}>
      {Array.isArray(treeData) && treeData.length > 0
        ? treeData.map((node) => renderTree(node))
        : "No data available"}
    </div>
  );
};

export default ExpandableMenu;
